import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { INDUSTRIES, industryLabel, isKnownIndustry } from "../lib/industries.js";
import { formatDateTimeMedium } from "../lib/datetime.js";
import { Badge, Button, Card, Modal, SegmentedControl, Select } from "../lib/ui.js";
import {
  PROMPT_KINDS,
  PROMPT_PLACEHOLDERS,
  PROMPT_TEMPLATE_LIMITS,
  describePromptCheck,
  renderPromptTemplate,
  validatePromptTemplate,
  type PromptKind,
} from "../lib/promptTemplates.js";

const GLOBAL = "global";
const ORIGINAL_NOTE = "Original (built-in)";

interface PromptRow {
  id: string;
  kind: PromptKind;
  industry: string | null;
  body: string;
  updated_at: string;
}

interface VersionRow {
  id: number;
  action: "saved" | "removed";
  note: string | null;
  saved_at: string;
  body: string;
}

interface BusinessRow {
  id: string;
  name: string;
  industry: string | null;
}

function errorMessage(message: string): string {
  if (message.includes("prompt_invalid:too_short")) return `That is too short to be the instructions (at least ${PROMPT_TEMPLATE_LIMITS.minChars} characters).`;
  if (message.includes("prompt_invalid:too_long")) return "That is too long.";
  const unknown = message.match(/prompt_invalid:unknown_placeholder:?([^\s"]*)/);
  if (unknown) return `"${unknown[1] ?? ""}" is not a placeholder this model knows.`;
  const missing = message.match(/prompt_invalid:missing_placeholder:([a-z0-9_]+)/);
  if (missing) return `The text must contain {{${missing[1]}}}.`;
  if (message.includes("prompt_global_cannot_be_removed")) return "The all-business text can't be removed; change it or load an earlier version instead.";
  if (message.includes("prompt_not_found")) return "That text no longer exists.";
  if (message.includes("industry_invalid")) return "That isn't a valid industry.";
  if (message.includes("not_authorized")) return "Only a platform admin can change these.";
  return message || "That couldn't be completed.";
}

/**
 * Voice instructions: the text each model starts every call with, editable by the platform admin. There are two models (the voice
 * model that speaks to the caller, and the backend model that decides and uses the booking tools) and, for each, one text for every
 * business and, optionally, one for an industry, which wins over the all-business text. The text has {{placeholders}} that are filled
 * in per call; a save is refused unless they are known ones and the required ones are kept (the same check the database makes), and
 * every save is kept so any earlier version can be brought back. Calls that start after a save use it, within about a minute.
 */
export function VoiceInstructions() {
  const [kind, setKind] = useState<PromptKind>("voice");
  const [scopeKey, setScopeKey] = useState<string>(GLOBAL);
  const [rows, setRows] = useState<PromptRow[]>([]);
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);

  const industry = scopeKey === GLOBAL ? null : scopeKey;

  const load = useCallback(async () => {
    const [{ data: promptRows }, { data: bizRows }] = await Promise.all([
      supabase.from("platform_prompts").select("id, kind, industry, body, updated_at"),
      supabase.from("businesses").select("id, name, industry").order("name"),
    ]);
    setRows((promptRows ?? []) as PromptRow[]);
    setBusinesses((bizRows ?? []) as BusinessRow[]);
    setLoading(false);
  }, []);

  const loadVersions = useCallback(async () => {
    let q = supabase.from("platform_prompt_versions").select("id, action, note, saved_at, body").eq("kind", kind);
    q = industry === null ? q.is("industry", null) : q.eq("industry", industry);
    const { data } = await q.order("saved_at", { ascending: false }).limit(30);
    setVersions((data ?? []) as VersionRow[]);
  }, [kind, industry]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  const own = rows.find((r) => r.kind === kind && r.industry === industry);
  const allBusinessesRow = rows.find((r) => r.kind === kind && r.industry === null);
  const baseText = own?.body ?? allBusinessesRow?.body ?? "";

  // The editor starts from what is saved for the chosen model and scope, and starts over when that changes (a save, a switch).
  useEffect(() => {
    setDraft(baseText);
    setNote("");
    setError(null);
  }, [kind, scopeKey, baseText]);

  useEffect(() => {
    if (pendingCursor.current !== null && box.current) {
      box.current.focus();
      box.current.setSelectionRange(pendingCursor.current, pendingCursor.current);
      pendingCursor.current = null;
    }
  }, [draft]);

  const options = useMemo(() => {
    const custom = [
      ...new Set([...businesses.map((b) => b.industry), ...rows.map((r) => r.industry)].filter((i): i is string => !!i && !isKnownIndustry(i))),
    ];
    return [...INDUSTRIES.map((i) => ({ value: i.value as string, label: i.label })), ...custom.map((c) => ({ value: c, label: `${c} (custom)` }))];
  }, [businesses, rows]);

  const hasOwn = (key: string) => rows.some((r) => r.kind === kind && r.industry === key);
  const dirty = draft.trim() !== baseText.trim();
  const check = validatePromptTemplate(kind, draft);
  const kindInfo = PROMPT_KINDS.find((k) => k.value === kind)!;
  const scopeLabel = industry === null ? "All businesses" : industryLabel(industry);
  const audience = industry === null ? businesses : businesses.filter((b) => b.industry === industry);
  const placeholders = PROMPT_PLACEHOLDERS[kind];

  function confirmDiscard(): boolean {
    if (!dirty) return true;
    return typeof window.confirm !== "function" || window.confirm("You have changes that are not saved. Discard them?");
  }

  function changeKind(next: PromptKind) {
    if (next === kind || !confirmDiscard()) return;
    setKind(next);
    setMessage(null);
  }

  function changeScope(next: string) {
    if (next === scopeKey || !confirmDiscard()) return;
    setScopeKey(next);
    setMessage(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_save_prompt", { p_kind: kind, p_industry: industry, p_body: draft, p_note: note.trim() || null });
    setBusy(false);
    if (err) {
      setError(errorMessage(err.message));
      return;
    }
    setMessage("Saved. Calls that start from now on use it (within about a minute).");
    await Promise.all([load(), loadVersions()]);
  }

  async function removeOwn() {
    setBusy(true);
    setError(null);
    setMessage(null);
    const { error: err } = await supabase.rpc("rpc_admin_remove_prompt", { p_kind: kind, p_industry: industry });
    setBusy(false);
    setConfirmRemove(false);
    if (err) {
      setError(errorMessage(err.message));
      return;
    }
    setMessage(`Removed. ${scopeLabel} uses the all-business text again.`);
    await Promise.all([load(), loadVersions()]);
  }

  function insertPlaceholder(key: string) {
    const tag = `{{${key}}}`;
    const el = box.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    pendingCursor.current = start + tag.length;
    setDraft(draft.slice(0, start) + tag + draft.slice(end));
  }

  function loadVersion(v: VersionRow) {
    setDraft(v.body);
    setMessage("Loaded into the editor. Save to make it live.");
    setError(null);
  }

  const preview = useMemo(
    () => renderPromptTemplate(draft, Object.fromEntries(placeholders.map((p) => [p.key, p.example]))),
    [draft, placeholders]
  );

  if (loading) return <p style={{ color: "var(--rc-text-muted)" }}>Loading...</p>;

  const saveDisabled = busy || !dirty || !check.ok;

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 4 }}>Voice instructions</h1>
      <p style={{ color: "var(--rc-text-muted)", fontSize: 13, margin: "0 0 16px", maxWidth: 780 }}>
        The text each model starts every call with, for every business or for one kind of business. Calls that start after you save use it (within about a
        minute). A mistake here can break calls, so a save is checked (only the placeholders listed here, and the required ones kept), and every version is
        kept below so you can bring an earlier one back. Rules about how to behave belong on Industry rules; they are added after this text automatically.
      </p>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Model</div>
          <SegmentedControl options={PROMPT_KINDS.map((k) => ({ value: k.value, label: k.label }))} value={kind} onChange={changeKind} />
        </div>
        <div>
          <label htmlFor="prompt-scope" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Instructions for
          </label>
          <Select id="prompt-scope" value={scopeKey} onChange={(e) => changeScope(e.target.value)} style={{ minWidth: 260 }}>
            <option value={GLOBAL}>All businesses</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {`${o.label}${hasOwn(o.value) ? " (own text)" : ""}`}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 16px" }}>
        {kindInfo.blurb}{" "}
        {industry === null
          ? `This text is used by every business that has no text of its own for its industry (${audience.length} businesses in all).`
          : audience.length === 0
            ? `No business is set to ${scopeLabel} yet, so this text applies to nobody. Set a business's industry on the Clients & businesses page.`
            : `Used by ${audience.length} ${audience.length === 1 ? "business" : "businesses"}: ${audience.map((b) => b.name).join(", ")}.`}
      </p>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              {industry !== null && (own ? <Badge tone="primary">{scopeLabel} has its own text</Badge> : <Badge tone="neutral">Using the all-business text</Badge>)}
              {dirty && <Badge tone="warning">Not saved</Badge>}
              {own && <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>Saved {formatDateTimeMedium(own.updated_at)}</span>}
            </div>
            {industry !== null && !own && (
              <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 8px" }}>
                {scopeLabel} has no text of its own, so its calls use the all-business text shown here. Change it and save to give {scopeLabel} its own.
              </p>
            )}
            <label htmlFor="prompt-text" className="rc-sr-only" style={{ position: "absolute", left: -9999 }}>
              Instructions text
            </label>
            <textarea
              id="prompt-text"
              ref={box}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={26}
              spellCheck={false}
              style={{ ...textareaStyle, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12.5, lineHeight: 1.5, minHeight: 360 }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--rc-text-muted)", marginTop: 4 }}>
              <span>
                {draft.trim().length.toLocaleString("en-AU")} of {PROMPT_TEMPLATE_LIMITS.maxChars.toLocaleString("en-AU")} characters
              </span>
              <button style={linkButton("var(--rc-primary)")} onClick={() => setShowPreview((s) => !s)}>
                {showPreview ? "Hide preview" : "Show preview"}
              </button>
            </div>
            {!check.ok && dirty && (
              <p role="alert" style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>
                {describePromptCheck(check)}
              </p>
            )}
            {error && (
              <p role="alert" style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>
                {error}
              </p>
            )}
            {message && <p style={{ color: "var(--rc-success)", fontSize: 12.5, margin: "8px 0 0" }}>{message}</p>}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <input
                aria-label="What did you change?"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                placeholder="What did you change? (optional, kept in the history)"
                style={{ ...textareaStyle, flex: "1 1 240px", width: "auto" }}
              />
              <Button variant="primary" onClick={save} disabled={saveDisabled}>
                {busy ? "Saving..." : industry === null ? "Save" : own ? `Save for ${scopeLabel}` : `Save as ${scopeLabel}'s own text`}
              </Button>
              {dirty && (
                <Button variant="secondary" onClick={() => setDraft(baseText)} disabled={busy}>
                  Discard changes
                </Button>
              )}
              {industry !== null && own && (
                <Button variant="danger" onClick={() => setConfirmRemove(true)} disabled={busy}>
                  Use the all-business text
                </Button>
              )}
            </div>
          </Card>

          {showPreview && (
            <Card style={{ marginBottom: 16 }}>
              <h2 className="rc-h2" style={{ margin: "0 0 4px" }}>Preview</h2>
              <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 8px" }}>
                The text with the placeholders filled in with examples. A real call fills in that call's own values.
              </p>
              <pre aria-label="Preview" style={{ ...preStyle, maxHeight: 420 }}>{preview}</pre>
            </Card>
          )}
        </div>

        <div style={{ flex: "0 1 320px", minWidth: 260 }}>
          <Card style={{ marginBottom: 16 }}>
            <h2 className="rc-h2" style={{ margin: "0 0 4px" }}>Placeholders</h2>
            <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>Filled in for each call. Click one to insert it where the cursor is.</p>
            {placeholders.map((p) => (
              <div key={p.key} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <button style={{ ...linkButton("var(--rc-primary)"), fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12.5 }} onClick={() => insertPlaceholder(p.key)}>
                    {`{{${p.key}}}`}
                  </button>
                  {p.required && <Badge tone="warning">required</Badge>}
                </div>
                <div style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>{p.description}</div>
              </div>
            ))}
          </Card>

          <Card>
            <h2 className="rc-h2" style={{ margin: "0 0 4px" }}>History</h2>
            <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>
              Every save for {kindInfo.label.toLowerCase()}, {scopeLabel}. Load one into the editor, then save to make it live.
            </p>
            {versions.length === 0 ? (
              <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: 0 }}>Nothing saved here yet.</p>
            ) : (
              versions.map((v) => (
                <div key={v.id} style={{ borderTop: "1px solid var(--rc-border)", padding: "8px 0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                    <span style={{ fontSize: 12.5 }}>{formatDateTimeMedium(v.saved_at)}</span>
                    <button style={linkButton("var(--rc-primary)")} onClick={() => loadVersion(v)}>
                      {v.note === ORIGINAL_NOTE ? "Restore original" : "Load"}
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
                    {v.action === "removed" ? "Removed (back to the all-business text)" : (v.note ?? "Saved")}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      {confirmRemove && (
        <Modal title={`Use the all-business text for ${scopeLabel}?`} onClose={() => setConfirmRemove(false)}>
          <p style={{ fontSize: 14, marginTop: 0 }}>
            {scopeLabel} will go back to the all-business text for the {kindInfo.label.toLowerCase()}. Its own text stays in the history, so you can load it again.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={removeOwn} disabled={busy}>
              Use the all-business text
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const textareaStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: 8,
  fontSize: 14,
  fontFamily: "inherit",
  border: "1px solid var(--rc-border-strong)",
  borderRadius: "var(--rc-radius-md)",
  background: "var(--rc-surface)",
  color: "var(--rc-text)",
};

const preStyle: React.CSSProperties = {
  margin: 0,
  padding: 10,
  overflow: "auto",
  whiteSpace: "pre-wrap",
  fontSize: 12,
  lineHeight: 1.5,
  background: "var(--rc-neutral-soft)",
  borderRadius: "var(--rc-radius-md)",
};

const linkButton = (color: string): React.CSSProperties => ({ background: "none", border: "none", padding: 0, color, cursor: "pointer", fontSize: 13, textAlign: "left" });
